import { ref } from 'vue'
import api from '@/api/client'
import type { LayerTreeNode } from '@/types/layerTree'

// ── Types ────────────────────────────────────────────────────────────────────

export interface FarmSummary {
  id: number
  name: string
  description: string
  /** [xmin, ymin, xmax, ymax] in EPSG:3857, or null if no boundary set */
  extent: [number, number, number, number] | null
  /** { lon, lat } centroid in WGS-84, or null */
  centroid: { lon: number; lat: number } | null
}

export interface FarmDetail extends FarmSummary {}

export interface FieldSummary {
  id: number
  name: string
  name_in_files: string | null
  /** [xmin, ymin, xmax, ymax] in EPSG:3857, or null if no boundary set */
  extent: [number, number, number, number] | null
  /** { lon, lat } centroid in WGS-84, or null */
  centroid: { lon: number; lat: number } | null
}

// ── Singleton state (shared across all useFarms() calls) ─────────────────────

const farms       = ref<FarmSummary[]>([])
const activeFarm  = ref<FarmDetail | null>(null)
const fields      = ref<FieldSummary[]>([])
const activeField = ref<FieldSummary | null>(null)
const layerTree   = ref<LayerTreeNode[]>([])
const loading     = ref(false)
const error       = ref<string | null>(null)

const config = window.__APP_CONFIG__ ?? {}

/**
 * Resolve relative source URLs (e.g. Wagtail document paths) to absolute
 * using the configured NGINX base URL.
 */
export function fixVectorUrl(url: string): string {
  if (!url) return ''
  return url.startsWith('/') ? `${config.NGINX_BASE_URL}${url}` : url
}

// ── Composable ────────────────────────────────────────────────────────────────

export function useFarms() {
  /** Fetch the lightweight list of farms for the logged-in user. */
  async function fetchFarms() {
    loading.value = true
    error.value   = null
    try {
      const res = await api.get<FarmSummary[]>('/map/farms/')
      farms.value = res.data
    } catch (e: any) {
      error.value = e.response?.status === 401
        ? 'Unauthorized – please log in.'
        : (e.message ?? 'Unknown error')
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch full detail for one farm, its fields, and its layer tree.
   * If the farm has fields, the first one is auto-selected and the tree is
   * resolved field-scoped; otherwise the farm-level tree is loaded.
   */
  async function selectFarm(id: number) {
    loading.value = true
    error.value   = null
    layerTree.value = []
    fields.value    = []
    activeField.value = null
    try {
      const [farmRes, fieldsRes] = await Promise.all([
        api.get<FarmDetail>(`/map/farms/${id}/`),
        api.get<FieldSummary[]>(`/map/farms/${id}/fields/`),
      ])
      const first = fieldsRes.data[0] ?? null
      const treeRes = await api.get<LayerTreeNode[]>(
        `/map/farms/${id}/layer-tree/`,
        first ? { params: { field: first.id } } : undefined,
      )
      // Assign reactive state together (after all awaits) so the layer tree is
      // already populated when the activeFarm/activeField watchers fire.
      activeFarm.value  = farmRes.data
      fields.value      = fieldsRes.data
      activeField.value = first
      layerTree.value   = absolutizeTreeUrls(treeRes.data)
    } catch (e: any) {
      error.value = e.response?.status === 401
        ? 'Unauthorized – please log in.'
        : (e.message ?? 'Unknown error')
    } finally {
      loading.value = false
    }
  }

  /** Switch the active field and re-fetch the field-scoped layer tree. */
  async function selectField(fieldId: number) {
    const farmId = activeFarm.value?.id
    if (farmId == null) return
    const field = fields.value.find(f => f.id === fieldId) ?? null
    if (!field) return
    loading.value = true
    error.value   = null
    try {
      const treeRes = await api.get<LayerTreeNode[]>(
        `/map/farms/${farmId}/layer-tree/`,
        { params: { field: fieldId } },
      )
      // Assign together so the watcher sees the new tree alongside the field.
      activeField.value = field
      layerTree.value   = absolutizeTreeUrls(treeRes.data)
    } catch (e: any) {
      error.value = e.response?.status === 401
        ? 'Unauthorized – please log in.'
        : (e.message ?? 'Unknown error')
    } finally {
      loading.value = false
    }
  }

  function clearActiveFarm() {
    activeFarm.value  = null
    fields.value      = []
    activeField.value = null
    layerTree.value   = []
  }

  return {
    farms, activeFarm, fields, activeField, layerTree, loading, error,
    fetchFarms, selectFarm, selectField, clearActiveFarm,
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function absolutizeTreeUrls(nodes: LayerTreeNode[]): LayerTreeNode[] {
  return nodes.map(node => {
    if (node.type === 'group') {
      return { ...node, children: absolutizeTreeUrls(node.children) }
    }
    if (node.type === 'raster_type') {
      return {
        ...node,
        cogs: node.cogs.map(cog => ({
          ...cog,
          // COG tile URL is relative (/api/map/tiles/…); OL resolves it
          // against the Vite dev-server origin, not the API origin.
          // Prepend NGINX_BASE_URL so OL always hits the correct host.
          tile_url: fixVectorUrl(cog.tile_url),
        })),
      }
    }
    if (node.type === 'vector') {
      return {
        ...node,
        vector_layers: node.vector_layers.map(vl => ({
          ...vl,
          source_url: fixVectorUrl(vl.source_url),
        })),
      }
    }
    return node
  })
}
